import React, { useState, useEffect, useCallback } from 'react';
import authService from '../../services/authService';
import ReAuthenticateModal from './ReAuthenticateModal';
import './EmployeeSecurityHub.css';

const EmployeeSecurityHub = ({
  isOpen,
  onClose,
  user,
  onOpenChangePassword
}) => {
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' | 'sessions' | 'history' | 'mfa'
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [successNotice, setSuccessNotice] = useState(null);

  // Security State
  const [passwordStatus, setPasswordStatus] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loginHistory, setLoginHistory] = useState([]);
  const [passkeys, setPasskeys] = useState([]);
  const [faceStatus, setFaceStatus] = useState(null);

  // Contact Information & Re-authentication state
  const [contactMobile, setContactMobile] = useState(user?.mobile || '');
  const [contactEmail, setContactEmail] = useState(user?.email || '');
  const [isEditingContact, setIsEditingContact] = useState(false);
  const [isSavingContact, setIsSavingContact] = useState(false);
  const [isReAuthModalOpen, setIsReAuthModalOpen] = useState(false);
  const [pendingContactPayload, setPendingContactPayload] = useState(null);

  // Logout other devices confirmation dialog
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isTerminatingOtherSessions, setIsTerminatingOtherSessions] = useState(false);

  const fetchSecurityData = useCallback(async (isSilent = false) => {
    if (!isSilent) setIsLoading(true);
    else setIsRefreshing(true);
    setError(null);

    try {
      const employeeId = user?.employeeId || user?.employeeID;

      // Parallel fetch of security state
      const [
        pwdRes,
        sessionsRes,
        historyRes,
        passkeyRes,
        faceRes
      ] = await Promise.allSettled([
        authService.getPasswordExpiryStatus(),
        authService.getMySessions(),
        authService.getMyLoginHistory(),
        employeeId ? authService.getPasskeyCredentials(employeeId) : Promise.resolve(null),
        employeeId ? authService.getFaceEnrollmentStatus(employeeId) : Promise.resolve(null)
      ]);

      if (pwdRes.status === 'fulfilled' && pwdRes.value?.success) {
        setPasswordStatus(pwdRes.value.data);
      }

      if (sessionsRes.status === 'fulfilled' && sessionsRes.value?.success) {
        setSessions(sessionsRes.value.data || []);
      }

      if (historyRes.status === 'fulfilled' && historyRes.value?.success) {
        setLoginHistory(historyRes.value.data || []);
      }

      if (passkeyRes.status === 'fulfilled' && passkeyRes.value?.success) {
        setPasskeys(passkeyRes.value.data || []);
      }

      if (faceRes.status === 'fulfilled' && faceRes.value?.success) {
        setFaceStatus(faceRes.value.data);
      }
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load security profile details.');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [user]);

  useEffect(() => {
    if (isOpen) {
      fetchSecurityData();
    }
  }, [isOpen, fetchSecurityData]);

  if (!isOpen) return null;

  const currentSession = sessions.find((s) => s.isCurrentSession) || sessions[0];
  const otherSessions = sessions.filter((s) => !s.isCurrentSession && s !== currentSession);

  const handleLogoutOtherDevices = async () => {
    setIsTerminatingOtherSessions(true);
    setError(null);
    setSuccessNotice(null);

    try {
      const res = await authService.logoutOtherDevices();
      if (res.success) {
        setSuccessNotice(res.message || 'All other active sessions have been terminated.');
        setShowLogoutConfirm(false);
        // Refresh session list
        await fetchSecurityData(true);
      } else {
        setError(res.message || 'Failed to terminate other sessions.');
      }
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to terminate remote sessions.');
    } finally {
      setIsTerminatingOtherSessions(false);
    }
  };

  const handleInitiateSaveContact = (e) => {
    e.preventDefault();
    setError(null);
    setSuccessNotice(null);

    const isMobileChanged = (contactMobile || '').trim() !== (user?.mobile || '').trim();
    const isEmailChanged = (contactEmail || '').trim() !== (user?.email || '').trim();

    if (!isMobileChanged && !isEmailChanged) {
      setIsEditingContact(false);
      return;
    }

    const payload = {
      mobile: contactMobile.trim(),
      email: contactEmail.trim()
    };

    setPendingContactPayload(payload);
    // Open re-authentication modal for step-up security verification
    setIsReAuthModalOpen(true);
  };

  const handleReAuthSuccess = async (reAuthToken) => {
    if (!pendingContactPayload) return;
    setIsSavingContact(true);
    setError(null);
    setSuccessNotice(null);

    try {
      const res = await authService.updateMyProfile(pendingContactPayload, reAuthToken);
      if (res.success) {
        setSuccessNotice('Contact details updated securely.');
        setIsEditingContact(false);
        if (user) {
          user.mobile = pendingContactPayload.mobile;
          user.email = pendingContactPayload.email;
        }
      } else {
        setError(res.message || 'Failed to update profile details.');
      }
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to update contact details.');
    } finally {
      setIsSavingContact(false);
      setPendingContactPayload(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      const d = new Date(dateStr);
      return d.toLocaleString(undefined, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="security-hub-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div
        className="security-hub-container fade-in"
        onClick={(e) => e.stopPropagation()}
        id="employee-security-hub-modal"
      >
        {/* Header */}
        <div className="security-hub-header">
          <div className="security-hub-title-wrap">
            <div className="security-hub-icon-badge">🛡️</div>
            <div>
              <h2 className="security-hub-title">Employee Security Hub</h2>
              <div className="security-hub-subtitle">
                <span>{user?.name || user?.userName || 'Employee Profile'}</span>
                <span className="dot-sep">•</span>
                <span className="badge-role">{user?.roleName || user?.role || 'Staff'}</span>
                <span className="dot-sep">•</span>
                <span>{user?.centreName || 'Main Lab'}</span>
              </div>
            </div>
          </div>
          <div className="security-hub-actions">
            <button
              type="button"
              className="security-hub-refresh-btn"
              onClick={() => fetchSecurityData(true)}
              disabled={isRefreshing || isLoading}
              title="Refresh security data"
            >
              <span className={`refresh-spin ${isRefreshing ? 'spinning' : ''}`}>🔄</span>
            </button>
            <button
              type="button"
              className="security-hub-close-btn"
              onClick={onClose}
              aria-label="Close"
              title="Close Security Hub"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="security-hub-tabs">
          <button
            type="button"
            className={`security-tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            📋 Security Overview
          </button>
          <button
            type="button"
            className={`security-tab-btn ${activeTab === 'sessions' ? 'active' : ''}`}
            onClick={() => setActiveTab('sessions')}
          >
            💻 Active Sessions ({sessions.length})
          </button>
          <button
            type="button"
            className={`security-tab-btn ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            📜 Login Activity
          </button>
          <button
            type="button"
            className={`security-tab-btn ${activeTab === 'mfa' ? 'active' : ''}`}
            onClick={() => setActiveTab('mfa')}
          >
            🔑 MFA & Biometrics
          </button>
        </div>

        {/* Feedback Messages */}
        {error && (
          <div className="security-hub-alert alert-danger">
            <span>⚠️</span>
            <span>{error}</span>
          </div>
        )}
        {successNotice && (
          <div className="security-hub-alert alert-success">
            <span>✅</span>
            <span>{successNotice}</span>
          </div>
        )}

        {/* Body Content */}
        <div className="security-hub-body">
          {isLoading ? (
            <div className="security-hub-loading">
              <div className="security-spinner" />
              <span>Loading security profile & session telemetry...</span>
            </div>
          ) : (
            <>
              {/* TAB 1: OVERVIEW */}
              {activeTab === 'overview' && (
                <div className="security-tab-content">
                  <div className="security-grid-2">
                    {/* Password Card */}
                    <div className="security-card">
                      <div className="security-card-header">
                        <div className="security-card-title">
                          <span>🔐</span> Password & Credentials
                        </div>
                        <button
                          type="button"
                          className="btn-sec-action"
                          onClick={() => {
                            if (onOpenChangePassword) {
                              onOpenChangePassword();
                            }
                          }}
                        >
                          Change Password
                        </button>
                      </div>
                      <div className="security-card-body">
                        <div className="sec-prop-row">
                          <span className="sec-prop-label">Policy Status</span>
                          <span className="sec-prop-val font-semibold">
                            {passwordStatus?.policyDays ? `${passwordStatus.policyDays} Days Rotation` : 'Standard Policy'}
                          </span>
                        </div>
                        <div className="sec-prop-row">
                          <span className="sec-prop-label">Expires On</span>
                          <span className="sec-prop-val">
                            {passwordStatus?.expiresAt ? formatDate(passwordStatus.expiresAt) : 'Never'}
                          </span>
                        </div>
                        <div className="sec-prop-row">
                          <span className="sec-prop-label">Days Remaining</span>
                          <span className="sec-prop-val">
                            {passwordStatus?.daysRemaining != null ? (
                              <span className={`badge-days ${passwordStatus.isWarning ? 'badge-warning' : 'badge-safe'}`}>
                                {passwordStatus.daysRemaining} days remaining
                              </span>
                            ) : (
                              'N/A'
                            )}
                          </span>
                        </div>
                        <div className="sec-prop-row">
                          <span className="sec-prop-label">Last Changed</span>
                          <span className="sec-prop-val">
                            {passwordStatus?.lastChangedDate ? formatDate(passwordStatus.lastChangedDate) : 'Not recorded'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* MFA Summary Card */}
                    <div className="security-card">
                      <div className="security-card-header">
                        <div className="security-card-title">
                          <span>🛡️</span> Authentication Methods
                        </div>
                        <button
                          type="button"
                          className="btn-sec-link"
                          onClick={() => setActiveTab('mfa')}
                        >
                          View Details →
                        </button>
                      </div>
                      <div className="security-card-body">
                        <div className="sec-prop-row">
                          <span className="sec-prop-label">FIDO2 Passkeys</span>
                          <span className="sec-prop-val">
                            {passkeys.length > 0 ? (
                              <span className="badge-status-green">✓ {passkeys.length} Registered</span>
                            ) : (
                              <span className="badge-status-gray">Not Registered</span>
                            )}
                          </span>
                        </div>
                        <div className="sec-prop-row">
                          <span className="sec-prop-label">Facial Biometrics</span>
                          <span className="sec-prop-val">
                            {faceStatus?.isEnrolled ? (
                              <span className="badge-status-green">
                                ✓ Enrolled {faceStatus.quality ? `(${faceStatus.quality}%)` : ''}
                              </span>
                            ) : (
                              <span className="badge-status-gray">Not Enrolled</span>
                            )}
                          </span>
                        </div>
                        <div className="sec-prop-row">
                          <span className="sec-prop-label">Primary Login</span>
                          <span className="sec-prop-val font-semibold">Password + Biometric / Passkey</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Active Sessions Mini Card */}
                  <div className="security-card mt-4">
                    <div className="security-card-header">
                      <div className="security-card-title">
                        <span>💻</span> Active Sessions ({sessions.length})
                      </div>
                      <button
                        type="button"
                        className="btn-sec-link"
                        onClick={() => setActiveTab('sessions')}
                      >
                        Manage Sessions →
                      </button>
                    </div>
                    <div className="security-card-body">
                      {currentSession ? (
                        <div className="current-device-box">
                          <div className="current-device-icon">💻</div>
                          <div className="current-device-info">
                            <div className="current-device-title">
                              <span>{currentSession.browser || 'Browser'} on {currentSession.operatingSystem || 'Device'}</span>
                              <span className="badge-current-pill">Current Device</span>
                            </div>
                            <div className="current-device-sub">
                              <span>IP: {currentSession.ipAddress || '127.0.0.1'}</span>
                              <span className="dot-sep">•</span>
                              <span>Signed in: {formatDate(currentSession.loginTime)}</span>
                              <span className="dot-sep">•</span>
                              <span className="status-active-text">Active Now</span>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="empty-state-text">No active session telemetry found.</div>
                      )}

                      {otherSessions.length > 0 && (
                        <div className="other-sessions-mini-summary">
                          <span>{otherSessions.length} other device(s) currently signed in.</span>
                          <button
                            type="button"
                            className="btn-danger-outline-sm"
                            onClick={() => setShowLogoutConfirm(true)}
                          >
                            Logout Other Devices
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Sensitive Contact & Profile Details Card */}
                  <div className="security-card mt-4">
                    <div className="security-card-header">
                      <div className="security-card-title">
                        <span>📱</span> Sensitive Contact & Security Profile
                      </div>
                      {!isEditingContact ? (
                        <button
                          type="button"
                          className="btn-sec-action"
                          onClick={() => setIsEditingContact(true)}
                        >
                          Edit Contact
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn-sec-link"
                          onClick={() => {
                            setIsEditingContact(false);
                            setContactMobile(user?.mobile || '');
                            setContactEmail(user?.email || '');
                          }}
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                    <div className="security-card-body">
                      {!isEditingContact ? (
                        <>
                          <div className="sec-prop-row">
                            <span className="sec-prop-label">Registered Mobile</span>
                            <span className="sec-prop-val font-semibold">{user?.mobile || 'Not provided'}</span>
                          </div>
                          <div className="sec-prop-row">
                            <span className="sec-prop-label">Registered Email</span>
                            <span className="sec-prop-val">{user?.email || 'Not provided'}</span>
                          </div>
                          <div className="sec-prop-row">
                            <span className="sec-prop-label">Protection Level</span>
                            <span className="sec-prop-val text-xs text-blue-600">
                              🔒 Step-Up Password Re-Authentication Protected
                            </span>
                          </div>
                        </>
                      ) : (
                        <form onSubmit={handleInitiateSaveContact} className="space-y-3">
                          <div className="sec-field-group">
                            <label className="sec-prop-label" htmlFor="contact-mobile-input">
                              Mobile Number
                            </label>
                            <input
                              id="contact-mobile-input"
                              type="tel"
                              className="reauth-input"
                              value={contactMobile}
                              onChange={(e) => setContactMobile(e.target.value)}
                              placeholder="e.g. 9876543210"
                              disabled={isSavingContact}
                            />
                          </div>
                          <div className="sec-field-group">
                            <label className="sec-prop-label" htmlFor="contact-email-input">
                              Email Address
                            </label>
                            <input
                              id="contact-email-input"
                              type="email"
                              className="reauth-input"
                              value={contactEmail}
                              onChange={(e) => setContactEmail(e.target.value)}
                              placeholder="e.g. user@hospital.com"
                              disabled={isSavingContact}
                            />
                          </div>
                          <div className="flex justify-end gap-2 pt-2">
                            <button
                              type="button"
                              className="btn-cancel"
                              onClick={() => {
                                setIsEditingContact(false);
                                setContactMobile(user?.mobile || '');
                                setContactEmail(user?.email || '');
                              }}
                              disabled={isSavingContact}
                            >
                              Cancel
                            </button>
                            <button
                              type="submit"
                              className="btn-sec-action"
                              disabled={isSavingContact}
                            >
                              {isSavingContact ? 'Saving...' : 'Save Changes'}
                            </button>
                          </div>
                        </form>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 2: ACTIVE SESSIONS */}
              {activeTab === 'sessions' && (
                <div className="security-tab-content">
                  <div className="sessions-header-toolbar">
                    <div>
                      <h3 className="section-heading">Active Devices & Workstations</h3>
                      <p className="section-description">
                        Review all browsers and devices that have access to your LIMS account. You can log out from all other devices remotely at any time.
                      </p>
                    </div>
                    {otherSessions.length > 0 && (
                      <button
                        type="button"
                        className="btn-logout-others"
                        onClick={() => setShowLogoutConfirm(true)}
                        id="btn-logout-other-devices"
                      >
                        🚪 Logout Other Devices ({otherSessions.length})
                      </button>
                    )}
                  </div>

                  {/* Current Session */}
                  <div className="session-section-title">Current Session</div>
                  {currentSession ? (
                    <div className="session-item-card current-session-card">
                      <div className="session-icon">🖥️</div>
                      <div className="session-details">
                        <div className="session-title-row">
                          <span className="session-name">
                            {currentSession.browser || 'Web Browser'} • {currentSession.operatingSystem || 'Workstation'}
                          </span>
                          <span className="badge-current-pill">This Device</span>
                        </div>
                        <div className="session-meta-grid">
                          <div><strong>IP Address:</strong> {currentSession.ipAddress || '127.0.0.1'}</div>
                          <div><strong>Centre:</strong> {currentSession.centreName || user?.centreName || 'Main Lab'}</div>
                          <div><strong>Login Time:</strong> {formatDate(currentSession.loginTime)}</div>
                          <div><strong>Last Activity:</strong> {formatDate(currentSession.lastActivityTime)}</div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="empty-state-card">No current session data found.</div>
                  )}

                  {/* Other Sessions */}
                  <div className="session-section-title mt-4">Other Active Sessions</div>
                  {otherSessions.length === 0 ? (
                    <div className="empty-state-card">
                      <div className="empty-icon">🛡️</div>
                      <div className="empty-text">You're only signed in on this device.</div>
                      <div className="empty-subtext">No other active workstations or mobile sessions exist for your account.</div>
                    </div>
                  ) : (
                    <div className="sessions-list">
                      {otherSessions.map((s, idx) => (
                        <div key={s.sessionId || idx} className="session-item-card">
                          <div className="session-icon">📱</div>
                          <div className="session-details">
                            <div className="session-title-row">
                              <span className="session-name">
                                {s.browser || 'Web Browser'} • {s.operatingSystem || 'Device'}
                              </span>
                              <span className="badge-status-green">Active</span>
                            </div>
                            <div className="session-meta-grid">
                              <div><strong>IP Address:</strong> {s.ipAddress || '—'}</div>
                              <div><strong>Centre:</strong> {s.centreName || '—'}</div>
                              <div><strong>Login Time:</strong> {formatDate(s.loginTime)}</div>
                              <div><strong>Last Activity:</strong> {formatDate(s.lastActivityTime)}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* TAB 3: LOGIN HISTORY */}
              {activeTab === 'history' && (
                <div className="security-tab-content">
                  <div className="sessions-header-toolbar">
                    <div>
                      <h3 className="section-heading">Recent Login Activity</h3>
                      <p className="section-description">
                        Displays the last 10 login sessions recorded for your employee account.
                      </p>
                    </div>
                  </div>

                  {loginHistory.length === 0 ? (
                    <div className="empty-state-card">
                      <div className="empty-icon">📜</div>
                      <div className="empty-text">No recent login activity found.</div>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="security-history-table">
                        <thead>
                          <tr>
                            <th>Login Timestamp</th>
                            <th>Status</th>
                            <th>Browser & OS</th>
                            <th>IP Address</th>
                            <th>Logout / Revocation</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loginHistory.map((item, idx) => {
                            return (
                              <tr key={item.sessionId || idx}>
                                <td className="font-semibold">{formatDate(item.loginTime)}</td>
                                <td>
                                  <span
                                    className={`status-pill ${
                                      item.status === 'ACTIVE'
                                        ? 'status-active'
                                        : item.status === 'FORCE_LOGGED_OUT'
                                        ? 'status-revoked'
                                        : 'status-closed'
                                    }`}
                                  >
                                    {item.status || 'SUCCESS'}
                                  </span>
                                </td>
                                <td>
                                  {item.browser || 'Browser'} on {item.operatingSystem || 'OS'}
                                </td>
                                <td><code>{item.ipAddress || '—'}</code></td>
                                <td className="text-muted">
                                  {item.logoutTime
                                    ? formatDate(item.logoutTime)
                                    : item.revocationReason || (item.status === 'ACTIVE' ? 'Active Session' : '—')}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* TAB 4: MFA & BIOMETRICS */}
              {activeTab === 'mfa' && (
                <div className="security-tab-content">
                  <div className="security-grid-2">
                    {/* Passkeys Card */}
                    <div className="security-card">
                      <div className="security-card-header">
                        <div className="security-card-title">
                          <span>🔑</span> FIDO2 / WebAuthn Passkeys
                        </div>
                      </div>
                      <div className="security-card-body">
                        {passkeys.length === 0 ? (
                          <div className="empty-state-text">
                            No passkey is currently registered.
                          </div>
                        ) : (
                          <div className="passkey-list">
                            {passkeys.map((pk) => (
                              <div key={pk.id} className="passkey-item">
                                <div className="passkey-meta">
                                  <div className="passkey-name">
                                    <span>🔐</span> {pk.deviceName || 'Security Key / Device'}
                                  </div>
                                  <div className="passkey-date">
                                    Registered: {formatDate(pk.createdDate)}
                                  </div>
                                </div>
                                <span className="badge-status-green">Active</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Facial Biometrics Card */}
                    <div className="security-card">
                      <div className="security-card-header">
                        <div className="security-card-title">
                          <span>📷</span> Facial Biometric Authentication
                        </div>
                      </div>
                      <div className="security-card-body">
                        {faceStatus?.isEnrolled ? (
                          <div className="biometric-status-box enrolled">
                            <div className="bio-icon">👤</div>
                            <div className="bio-info">
                              <div className="bio-title font-semibold">Face Biometric Enrolled</div>
                              <div className="bio-sub">
                                <span>Quality: {faceStatus.quality || 95}%</span>
                                {faceStatus.enrolledDate && (
                                  <>
                                    <span className="dot-sep">•</span>
                                    <span>Enrolled: {formatDate(faceStatus.enrolledDate)}</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="empty-state-text">
                            Face authentication is not enrolled.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="security-hub-footer">
          <div className="security-footer-info">
            🔒 Protected by Enterprise Zero-Trust Session & Multi-Factor Guard
          </div>
          <button type="button" className="btn-sec-close" onClick={onClose}>
            Close
          </button>
        </div>

        {/* Confirmation Modal: Logout Other Devices */}
        {showLogoutConfirm && (
          <div className="logout-confirm-overlay" onClick={() => setShowLogoutConfirm(false)}>
            <div className="logout-confirm-card fade-in" onClick={(e) => e.stopPropagation()}>
              <div className="logout-confirm-header">
                <span className="logout-icon-alert">⚠️</span>
                <h3>Log out from other devices?</h3>
              </div>
              <p className="logout-confirm-body">
                This will terminate all {otherSessions.length} other active session(s) on remote workstations, tablets, or phones.
                <br /><br />
                <strong>Your current session on this device will remain active.</strong>
              </p>
              <div className="logout-confirm-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setShowLogoutConfirm(false)}
                  disabled={isTerminatingOtherSessions}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-confirm-logout"
                  onClick={handleLogoutOtherDevices}
                  disabled={isTerminatingOtherSessions}
                  id="confirm-logout-other-devices-btn"
                >
                  {isTerminatingOtherSessions ? 'Terminating Sessions...' : 'Logout Other Devices'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Re-Authentication Modal for Sensitive Actions */}
        <ReAuthenticateModal
          isOpen={isReAuthModalOpen}
          onClose={() => {
            setIsReAuthModalOpen(false);
            setPendingContactPayload(null);
          }}
          onSuccess={handleReAuthSuccess}
          reason="save changes to sensitive profile contact information"
        />
      </div>
    </div>
  );
};

export default EmployeeSecurityHub;
